import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Anchor,
  Container,
  Group,
  Stack,
  Alert,
  Image,
  Modal,
  Stepper,
  PinInput,
  LoadingOverlay,
  Grid,
  Box,
  BackgroundImage,
  Center,
  rem,
  ScrollArea,
  Card,
  Badge
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconLock, IconMail, IconInfoCircle, IconSpeakerphone } from '@tabler/icons-react';
import Swal from 'sweetalert2';

// --- ASSETS ---
import bgImage from '../../assets/bg.jpg'; 

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [capsLockOn, setCapsLockOn] = useState(false);

  // --- FORGOT PASSWORD STATE ---
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [resetStep, setResetStep] = useState(0);
  const [otpEmail, setOtpEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  // --- ANNOUNCEMENT STATE ---
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  // --- FETCH ANNOUNCEMENTS ON LOAD ---
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const { data } = await axios.get('/api/announcements/public');
        if (data && data.length > 0) {
          setAnnouncements(data);
          setAnnouncementModalOpen(true);
        }
      } catch (err) {
        console.error("Failed to fetch announcements", err);
      }
    };
    fetchAnnouncements();
  }, []);

  // --- LOGIN FORM HANDLER ---
  const loginForm = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (val) => (/^\S+@\S+$/.test(val) ? null : 'Invalid email'),
      password: (val) => (val.length <= 0 ? 'Password is required' : null),
    },
  });

  // --- HANDLERS ---
  const handleKeyUp = (e) => {
    setCapsLockOn(e.getModifierState('CapsLock'));
  };

  const onLoginSubmit = async (values) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/api/auth/login', values);

      if (response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
        
        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        Toast.fire({
          icon: 'success',
          title: 'Signed in successfully'
        });

        if (response.data.role === 'resident') {
          navigate('/resident-dashboard');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // --- FORGOT PASSWORD LOGIC ---
  const handleSendOtp = async () => {
    if (!/^\S+@\S+$/.test(otpEmail)) return Swal.fire('Error', 'Enter a valid email', 'error');
    setOtpLoading(true);
    try {
      await axios.post('/api/auth/forgotpassword', { email: otpEmail });
      Swal.fire('OTP Sent!', 'Check your email for the code.', 'success');
      setResetStep(1); 
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to send OTP', 'error');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetOtp || !newPassword) return Swal.fire('Error', 'Fill all fields', 'error');
    setOtpLoading(true);
    try {
      await axios.put('/api/auth/resetpassword', {
        email: otpEmail,
        otp: resetOtp,
        newPassword: newPassword
      });
      Swal.fire('Success', 'Password updated! Please login.', 'success');
      setForgotModalOpen(false);
      setResetStep(0);
      setOtpEmail('');
      setResetOtp('');
      setNewPassword('');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setOtpLoading(false);
    }
  };

  // --- STYLES ---
  const pageStyle = {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#1A1B1E', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: rem(20),
  };

  const cardStyle = {
    overflow: 'hidden',
    backgroundColor: '#25262B', 
    border: '1px solid #373A40',
    width: '100%',
    maxWidth: '1000px',
    borderRadius: '8px'
  };

  const inputStyles = {
    label: { color: '#C1C2C5' },
    input: { 
      backgroundColor: '#1A1B1E', 
      borderColor: '#373A40', 
      color: 'white' 
    }
  };

  const getPriorityColor = (p) => (p === 'urgent' ? 'red' : p === 'high' ? 'orange' : 'blue');

  return (
    <Box style={pageStyle}>
      <Container size={1000} p={0} style={{ width: '100%' }}>
        <Paper shadow="xl" radius="lg" style={cardStyle}>
          
          <Grid gutter={0}>
            
            {/* LEFT SIDE: IMAGE & BRANDING */}
            {/* visibleFrom="sm" hides this entire column on mobile (base) and shows it on tablet/desktop (sm) */}
            <Grid.Col span={{ base: 12, sm: 6 }} visibleFrom="sm" style={{ minHeight: '500px' }}>
              <Box style={{ height: '100%', position: 'relative' }}>
                <BackgroundImage
                  src={bgImage}
                  radius="0"
                  style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
                >
                  {/* Dark Gradient Overlay */}
                  <Box style={{ 
                    position: 'absolute', 
                    top: 0, left: 0, right: 0, bottom: 0, 
                    background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0, 50, 50, 0.9) 100%)',
                    zIndex: 0
                  }} />
                  
                  <Stack p="xl" style={{ position: 'relative', zIndex: 1 }}>
                    <Title order={1} style={{ fontWeight: 900, fontSize: rem(32), color: '#fff' }}>
                      Welcome Back
                    </Title>
                    <Text size="lg" style={{ color: '#C1C2C5' }}>
                      Manage your home, payments, and community with BALIHAI.
                    </Text>
                  </Stack>
                </BackgroundImage>
              </Box>
            </Grid.Col>

            {/* RIGHT SIDE: LOGIN FORM */}
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <Stack justify="center" p={{ base: 'xl', sm: 40 }} style={{ height: '100%', minHeight: '100%' }}>
                
                <Group justify="space-between" mb="md">
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    Secure Access
                  </Text>
                  <Image src="/logo.png" w={80} fit="contain" />
                </Group>

                <Title order={2} mb="md" style={{ color: 'white' }}>
                  Sign In to Account
                </Title>

                {error && (
                  <Alert icon={<IconAlertCircle size={16} />} title="Authentication Error" color="red" variant="filled">
                    {error}
                  </Alert>
                )}

                <form onSubmit={loginForm.onSubmit(onLoginSubmit)}>
                  <Stack gap="md">
                    <TextInput
                      label="Email Address"
                      placeholder="your@email.com"
                      leftSection={<IconMail size={18} />}
                      size="md"
                      styles={inputStyles}
                      {...loginForm.getInputProps('email')}
                    />
                    
                    <Box>
                      <PasswordInput
                        label="Password"
                        placeholder="Your password"
                        leftSection={<IconLock size={18} />}
                        size="md"
                        onKeyUp={handleKeyUp}
                        onKeyDown={handleKeyUp}
                        styles={{
                            ...inputStyles,
                            innerInput: { color: 'white' }
                        }}
                        {...loginForm.getInputProps('password')}
                      />
                      {capsLockOn && (
                        <Text c="yellow" size="xs" mt={5} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                           <IconInfoCircle size={12}/> Warning: Caps Lock is ON
                        </Text>
                      )}
                    </Box>

                    <Group justify="flex-end" mt="xs">
                      <Anchor 
                        component="button" 
                        type="button" 
                        c="teal" 
                        size="sm"
                        onClick={() => setForgotModalOpen(true)}
                      >
                        Forgot password?
                      </Anchor>
                    </Group>

                    <Button 
                      type="submit" 
                      fullWidth 
                      size="md" 
                      color="teal" 
                      loading={loading}
                      tt="uppercase"
                    >
                      Sign In
                    </Button>
                  </Stack>
                </form>
              </Stack>
            </Grid.Col>
          </Grid>
        </Paper>
      </Container>

      {/* --- FORGOT PASSWORD MODAL --- */}
      <Modal 
        opened={forgotModalOpen} 
        onClose={() => { setForgotModalOpen(false); setResetStep(0); }} 
        title="Reset Password"
        centered
        radius="md"
      >
        <Box pos="relative">
          <LoadingOverlay visible={otpLoading} overlayProps={{ blur: 2 }} />
          
          <Stepper active={resetStep} onStepClick={setResetStep} breakpoint="sm" color="teal" size="xs" mb="lg">
            <Stepper.Step label="Email" description="Send OTP" icon={<IconMail size={16} />} />
            <Stepper.Step label="Reset" description="New Password" icon={<IconLock size={16} />} />
          </Stepper>

          {resetStep === 0 && (
            <Stack>
              <Text size="sm" c="dimmed">
                Enter your registered email address. We will send you a One-Time Password (OTP) to reset your credentials.
              </Text>
              <TextInput
                label="Email Address"
                placeholder="your@email.com"
                value={otpEmail}
                onChange={(e) => setOtpEmail(e.target.value)}
                leftSection={<IconMail size={16}/>}
              />
              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setForgotModalOpen(false)}>Cancel</Button>
                <Button color="teal" onClick={handleSendOtp}>Send OTP Code</Button>
              </Group>
            </Stack>
          )}

          {resetStep === 1 && (
            <Stack>
              <Text size="sm" c="dimmed">
                Check your email for the 6-digit code.
              </Text>
              
              <Text size="sm" fw={500}>Enter OTP Code</Text>
              <Center>
                <PinInput 
                  length={6} 
                  type="number" 
                  size="md" 
                  value={resetOtp} 
                  onChange={setResetOtp} 
                />
              </Center>

              <PasswordInput
                label="New Password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.currentTarget.value)}
                mt="md"
              />

              <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={() => setResetStep(0)}>Back</Button>
                <Button color="teal" onClick={handleResetPassword}>Change Password</Button>
              </Group>
            </Stack>
          )}
        </Box>
      </Modal>

      {/* --- ANNOUNCEMENT POPUP MODAL --- */}
      <Modal 
        opened={announcementModalOpen} 
        onClose={() => setAnnouncementModalOpen(false)} 
        title={
            <Group>
                <IconSpeakerphone size={24} color="blue" />
                <Text fw={700} size="lg">Community Announcements</Text>
            </Group>
        }
        size="lg"
        centered
        overlayProps={{ opacity: 0.5, blur: 4 }}
      >
        <ScrollArea h={400} type="auto">
            <Stack gap="md">
                {announcements.map((item) => (
                    <Card key={item._id} withBorder shadow="sm" padding="md" radius="md" style={{ backgroundColor: '#f8f9fa' }}>
                        <Group justify="space-between" mb="xs">
                            <Text fw={600} size="lg" c="dark">{item.title}</Text>
                            <Group>
                                <Badge color={getPriorityColor(item.priority)}>{item.priority.toUpperCase()}</Badge>
                                {item.targetAudience !== 'all' && (
                                    <Badge variant="outline" color="gray">To: {item.targetAudience.toUpperCase()}</Badge>
                                )}
                            </Group>
                        </Group>
                        <Text size="xs" c="dimmed" mb="sm">{new Date(item.createdAt).toLocaleDateString()}</Text>
                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{item.details}</Text>
                    </Card>
                ))}
            </Stack>
        </ScrollArea>
        <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setAnnouncementModalOpen(false)}>Close</Button>
        </Group>
      </Modal>

    </Box>
  );
};

export default Login;